import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { routeTree } from './routeTree.gen';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ThemeProvider } from './components/theme';
import { initAnalytics, trackEvent, AnalyticsEvents } from './lib/analytics';
import './index.css';

// Defer analytics initialization to after critical rendering
// This improves FCP and LCP by not blocking on third-party scripts
if (typeof window !== 'undefined') {
  // Use requestIdleCallback if available, otherwise setTimeout
  const deferAnalytics = window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 1));
  deferAnalytics(() => {
    initAnalytics();
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (error) => {
        console.error('Mutation error:', error);
        // Track error in analytics
        trackEvent(AnalyticsEvents.ERROR_OCCURRED, {
          error_type: 'mutation_error',
          error_message: error instanceof Error ? error.message : String(error),
        });
      },
    },
  },
});

const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <ErrorBoundary
        onError={(error, errorInfo) => {
          // Log to console in development
          if (import.meta.env.DEV) {
            console.error('ErrorBoundary caught error:', error, errorInfo);
          }
          // Track error in analytics
          trackEvent(AnalyticsEvents.ERROR_OCCURRED, {
            error_type: 'react_error_boundary',
            error_message: error instanceof Error ? error.message : String(error),
            error_stack: error instanceof Error ? error.stack : undefined,
            component_stack: errorInfo?.componentStack,
          });
        }}
      >
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
}
